/**
 * Tic-Tac-Toe engine.
 *
 * Two-player perfect-information game; a 3×3 grid, first to three-in-a-row.
 * State is kept minimal so it round-trips cleanly through MLS:
 *   { board: (0|1|null)[9], turnIndex, players, terminal }
 * Moves are `{ cell: 0..8 }`.
 */
import type { UserId } from '@chat/protocol';
import type { GameComponentProps, GameEngine, GameTerminal } from './engine.js';

export interface TicTacToeState {
  readonly board: readonly (number | null)[];
  readonly turnIndex: number;
  readonly players: readonly UserId[];
  readonly terminal: GameTerminal | null;
}

export interface TicTacToeMove {
  readonly cell: number;
}

const LINES: readonly (readonly [number, number, number])[] = [
  [0, 1, 2],
  [3, 4, 5],
  [6, 7, 8],
  [0, 3, 6],
  [1, 4, 7],
  [2, 5, 8],
  [0, 4, 8],
  [2, 4, 6],
];

function evaluate(
  board: readonly (number | null)[],
  players: readonly UserId[],
): GameTerminal | null {
  for (const [a, b, c] of LINES) {
    const va = board[a];
    if (va !== null && va !== undefined && va === board[b] && va === board[c]) {
      const winner = players[va];
      if (winner) return { winner };
    }
  }
  if (board.every((cell) => cell !== null)) return { draw: true };
  return null;
}

export const ticTacToe: GameEngine<TicTacToeState, TicTacToeMove> = {
  id: 'tic-tac-toe',
  displayName: 'Tic-Tac-Toe',
  minPlayers: 2,
  maxPlayers: 2,

  initialState(players) {
    if (players.length < 2) throw new Error('tic-tac-toe needs two players');
    return {
      board: Array.from({ length: 9 }, () => null),
      turnIndex: 0,
      players: players.slice(0, 2),
      terminal: null,
    };
  },

  validateMove(state, move, by) {
    if (state.terminal) return 'game is over';
    const playerIndex = state.players.indexOf(by);
    if (playerIndex < 0) return 'spectator — not your game';
    if (playerIndex !== state.turnIndex) return 'not your turn';
    if (!Number.isInteger(move.cell) || move.cell < 0 || move.cell > 8) {
      return 'invalid cell';
    }
    if (state.board[move.cell] !== null) return 'cell already taken';
    return null;
  },

  applyMove(state, move, by) {
    const playerIndex = state.players.indexOf(by);
    const board = state.board.slice();
    board[move.cell] = playerIndex;
    const terminal = evaluate(board, state.players);
    return {
      ...state,
      board,
      turnIndex: terminal ? state.turnIndex : (state.turnIndex + 1) % state.players.length,
      terminal,
    };
  },

  currentTurn(state) {
    if (state.terminal) return null;
    return state.players[state.turnIndex] ?? null;
  },

  terminal(state) {
    return state.terminal;
  },

  isPlayer(state, user) {
    return state.players.includes(user);
  },

  Component: TicTacToeBoard,
};

function TicTacToeBoard({
  state,
  self,
  onMove,
  disabled,
}: GameComponentProps<TicTacToeState, TicTacToeMove>) {
  const myIndex = state.players.indexOf(self);
  const spectator = myIndex < 0;
  const canMove = !disabled && !spectator && !state.terminal && state.turnIndex === myIndex;

  const statusText = state.terminal
    ? state.terminal.draw
      ? 'draw'
      : state.terminal.winner === self
        ? 'you won'
        : 'you lost'
    : spectator
      ? 'spectating'
      : canMove
        ? 'your turn'
        : 'waiting for opponent';

  return (
    <section data-testid="ttt-board">
      <p data-testid="ttt-status">{statusText}</p>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 48px)',
          gap: 2,
          width: 'max-content',
        }}
      >
        {state.board.map((cell, i) => {
          const marker = cell === null ? '' : cell === 0 ? 'X' : 'O';
          return (
            <button
              key={i}
              data-testid={`ttt-cell-${String(i)}`}
              type="button"
              disabled={!canMove || cell !== null}
              onClick={() => {
                if (!canMove || cell !== null) return;
                onMove({ cell: i });
              }}
              style={{
                width: 48,
                height: 48,
                fontSize: 24,
                fontFamily: 'monospace',
              }}
            >
              {marker}
            </button>
          );
        })}
      </div>
    </section>
  );
}
